import { PaymentErrorType, PrepaidPaymentErrorSnapshot } from '@models/error';
import { GiftItemType } from '@models/gift';
import { useFlow } from '@stackflow/react';
import { orderActions } from '@store/order/orderSlice';
import { getGiftOrderRestoreParams } from '@utils/restoreGiftOrderUtils';
import { getTempOrder } from '@utils/restoreOrderUtils';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

interface Props {
    type: PaymentErrorType;
    payload: string;
}

function usePaymentFailed({ type, payload }:Props) {
    const { t } = useTranslation('errors');
    const { push, replace } = useFlow();
    const dispatch = useDispatch();
    const lng = navigator.language;
    const [cookies, , deleteCookie] = useCookies(['giftOrder'], { doNotParse: true });

    const handleTry = (func: Function) => {
        try {
            func();
        } catch (e) {
            console.error(e);
            replace('MainActivity', {});
            lazyToast(t('toast.dataLoadError', { lng }));
        }
    };
    const handleOnPrepayCharge = () => {
        handleTry(() => {
            const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
            const { prepaidCardId, amount } = data;
            if (prepaidCardId) {
                replace('MainActivity', {}, { animate: false });
                push('MyPrepaidCardActivity', {}, { animate: false });
                push('PrepaidCardChargeActivity', { prepaidCardId, amount });
                lazyToast(t('toast.prepaidChargeError', { lng }));
            } else {
                replace('MainActivity', {});
                lazyToast(t('toast.prepaidNotFound', { lng }));
            }
        });
    };

    const handleOnPrepayPurchase = () => {
        handleTry(() => {
            const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
            const { cardImageId, cardName, amount } = data;
            if (cardImageId) {
                toast.info(t('toast.prepaidPurchaseError', { lng }));
                replace('MainActivity', {}, { animate: false });
                push('MyPrepaidCardActivity', {}, { animate: false });
                push('PrepaidCardImagesActivity', { type: 'BUY' }, { animate: false });
                push('PrepaidCardImageActivity', {
                    prepaidCardImageId: cardImageId,
                    cardName,
                    amount,
                });
            } else {
                replace('MainActivity', {});
                lazyToast(t('toast.prepaidNotFound', { lng }));
            }
        });
    };

    const handleOnPrepayFail = () => {
        replace('MainActivity', {});
        lazyToast(t('toast.paymentError', { lng }));
    };

    const handleOnOrderFail = async () => {
        try {
            toast.info(t('toast.paymentCancelled', { lng }));
            const tempOrder = (await getTempOrder('ORDER'))!;
            const { shopId } = tempOrder;
            replace('MainActivity', {}, { animate: false });
            push('ShopListActivity', { type: 'LIST', isOrder: true }, { animate: false });
            push('ItemsActivity', { type: 'ALL', shopId }, { animate: false });
            push('OrderActivity', { }, { animate: false });
            dispatch(orderActions.copy(tempOrder));
        } catch (e) {
            console.error(e);
            replace('MainActivity', {});
            lazyToast(t('toast.dataLoadError', { lng }));
        }
    };

    const handleGiftFail = () => {
        const params = getGiftOrderRestoreParams(cookies.giftOrder);
        replace('MainActivity', {}, { animate: false });
        if (params) {
            if (params.type === GiftItemType.PRODUCT) {
                push('GiftItemActivity', { giftItemId: params.giftItemId }, { animate: false });
            }
            push('GiftOrderActivity', params);
            deleteCookie('giftOrder', { path: '/' });
        } else {
            push('GiftsActivity', { type: 'MAIN' });
        }
        lazyToast(t('toast.giftPaymentError', { lng }));
    };

    useEffect(() => {
        switch (type) {
        case 'PREPAY_CHARGE':
            handleOnPrepayCharge();
            break;
        case 'PREPAY_PURCHASE':
            handleOnPrepayPurchase();
            break;
        case 'PREPAY_FAIL':
            handleOnPrepayFail();
            break;
        case 'GIFT':
            handleGiftFail();
            break;
        case 'ORDER':
            handleOnOrderFail();
            break;
        default:
            throw new Error(t('toast.unsupportedPaymentType'));
        }
    }, [type, payload, t]);
}

function lazyToast(message: string) {
    setTimeout(() => {
        toast.error(message);
    }, 200);
}

export default usePaymentFailed;
